import * as React from 'react'
import { Button } from '../../components/Button'
import { styled } from '../../theme/styled'
import { ChevronIcon } from '../icons/ChevronIcon'
import PaginationProps from './PaginationProps'

const ButtonLine = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 0 16px;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1rem 0 2rem 0;
  & button {
    font-weight: 400;
  }
`

const NumberButton = styled(Button)<{ $active?: boolean; $size?: PaginationProps['size'] }>`
  background: ${(p) => (p.$active ? '#f3f3fa' : p.theme.av8.background)};
  border-radius: 4px;
  border: ${(p) => (p.$active ? 'none' : `1px solid ${p.theme.av8.borderColor}`)};
  width: initial;
  max-width: initial;
  padding: 0.5rem;
  min-width: 2rem;
  max-height: 2rem;
  min-height: 2rem;
  ${(p) => (p.$size === 'small' ? 'margin: 0 0.25rem;' : 'margin: 0 0.5rem;')}
`

const NavigationButton = styled(NumberButton)`
  color: ${(p) => p.theme.av8.primaryColor};
  width: 2rem;
  height: 2rem;
  padding: 0;
  &:hover {
    background: ${(p) => p.theme.av8.backgroundAlt};
  }
`

const LastPageButton = styled(NumberButton)<{ $extraMargin: boolean }>`
  ${(p) =>
    p.$extraMargin ? (p.$size === 'small' ? 'margin-left: 1.5rem;' : 'margin-left: 2rem;') : ''}
`

const calcPages = ({
  currentPage,
  totalPages,
  extraPages
}: {
  currentPage: number
  totalPages: number
  extraPages: number
}): number[] => {
  if (!totalPages) return [1]

  if (totalPages - currentPage < extraPages + 1) {
    return new Array(totalPages - currentPage).fill(0).map((_, i) => i + currentPage)
  }

  return new Array(extraPages + 1).fill(0).map((_, i) => i + currentPage)
}

const Pagination = React.memo((props: PaginationProps) => {
  const { currentPage, totalPages, onPageClick, size, numberOfExtraPages = 3 } = props
  const pages = calcPages({ currentPage, totalPages, extraPages: numberOfExtraPages })

  const buttons: React.ReactNode[] = pages.map((pageNumber) => {
    return (
      <NumberButton
        key={`btn-${pageNumber}`}
        onClick={() => {
          onPageClick(pageNumber)
        }}
        $active={pageNumber === currentPage}
        $size={size}
      >
        {pageNumber}
      </NumberButton>
    )
  })

  const nearLastPage = React.useMemo(() => {
    return pages.slice(-1)[0] === totalPages - 1
  }, [pages, totalPages])

  return (
    <ButtonContainer>
      <NavigationButton
        role='button'
        data-name='Previous'
        disabled={currentPage === 1}
        onClick={() => (currentPage > 1 ? onPageClick(currentPage - 1) : null)}
        $size={size}
      >
        <ChevronIcon rotate={180} />
      </NavigationButton>
      <ButtonLine>
        {buttons}
        <LastPageButton
          onClick={() => onPageClick(totalPages)}
          $extraMargin={!nearLastPage}
          $size={size}
        >
          {totalPages}
        </LastPageButton>
      </ButtonLine>
      <NavigationButton
        role='button'
        data-name='Next'
        disabled={currentPage === totalPages}
        onClick={() => (currentPage < totalPages ? onPageClick(currentPage + 1) : null)}
        $size={size}
      >
        <ChevronIcon />
      </NavigationButton>
    </ButtonContainer>
  )
})

export default Pagination
